<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>
        Back to Organizations
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Organization Details -->
    <div *ngIf="!loading && organization">
      <!-- Header Section -->
      <div class="card mb-4">
        <div class="card-body p-3 p-lg-4">
          <div class="row align-items-center">
            <div class="col-auto">
              <div class="entity-avatar entity-avatar-lg entity-avatar-square">
                <i class="bi bi-building"></i>
              </div>
            </div>
            <div class="col">
              <h2 class="mb-1 text-break">
                {{ organization.name }}
                <span *ngIf="organization.archivedAt" class="badge bg-warning text-dark ms-2">Archived</span>
              </h2>
              <div class="d-flex flex-wrap align-items-center column-gap-3 row-gap-1 text-muted">
                <span *ngIf="ownerProfile" class="text-break">
                  <i class="bi bi-person-circle me-1"></i>
                  Owner: {{ ownerProfile.firstName }} {{ ownerProfile.lastName }} ({{ ownerProfile.email }})
                </span>
                <span *ngIf="ownerProfile" class="d-none d-md-inline">|</span>
                <span>
                  <i class="bi bi-calendar-check me-1"></i>
                  Created: {{ formatShortDate(organization.createdAt) }}
                </span>
              </div>
            </div>
            <div class="col-auto">
              <a *ngIf="!organization.setupComplete" [routerLink]="['/organizations', organizationId, 'setup']" class="btn btn-outline-primary me-2">
                <i class="bi bi-gear me-2"></i>
                Continue Setup
              </a>
              <button type="button" class="btn btn-primary me-2" (click)="editOrganization()">
                <i class="bi bi-pencil me-2"></i>
                Edit
              </button>
              <button *ngIf="!organization.archivedAt" type="button" class="btn btn-outline-warning me-2" (click)="archiveOrganization()">
                <i class="bi bi-archive me-2"></i>Archive
              </button>
              <button *ngIf="organization.archivedAt" type="button" class="btn btn-warning me-2" (click)="unarchiveOrganization()">
                <i class="bi bi-archive me-2"></i>Restore
              </button>
              <a class="btn btn-outline-primary me-2" [routerLink]="['/dji-flights']" [queryParams]="{ organizationId: organizationId }">
                <i class="bi bi-airplane me-2"></i>DJI flights
              </a>
              <button type="button" class="btn btn-outline-danger" (click)="deleteOrganization()">
                <i class="bi bi-trash me-2"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <div class="card">
        <div class="card-header p-0">
          <ul id="org-detail-tabs" class="nav nav-underline px-3 pt-2 flex-nowrap flex-md-wrap overflow-x-auto" role="tablist">
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'overview'"
                type="button"
                role="tab"
                (click)="setActiveTab('overview')"
              >
                <i class="bi bi-grid me-2"></i>
                Overview
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'members'"
                type="button"
                role="tab"
                (click)="setActiveTab('members')"
              >
                <i class="bi bi-people me-2"></i>
                Members
                <span class="badge bg-secondary ms-2">{{ tabCounts?.members }}</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'drones'"
                type="button"
                role="tab"
                (click)="setActiveTab('drones')"
              >
                <i class="bi bi-truck me-2"></i>
                Drones
                <span class="badge bg-secondary ms-2">{{ tabCounts?.drones }}</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'certifications'"
                type="button"
                role="tab"
                (click)="setActiveTab('certifications')"
              >
                <i class="bi bi-patch-check me-2"></i>
                Certifications
                <span class="badge bg-secondary ms-2">{{ tabCounts?.certifications }}</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'sprayRecords'"
                type="button"
                role="tab"
                (click)="setActiveTab('sprayRecords')"
              >
                <i class="bi bi-journal-text me-2"></i>
                Spray Records
                <span class="badge bg-secondary ms-2">{{ tabCounts?.sprayRecords }}</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'customers'"
                type="button"
                role="tab"
                (click)="setActiveTab('customers')"
              >
                <i class="bi bi-person-lines-fill me-2"></i>
                Customers
                <span class="badge bg-secondary ms-2">{{ tabCounts?.customers }}</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'safetyTests'"
                type="button"
                role="tab"
                (click)="setActiveTab('safetyTests')"
              >
                <i class="bi bi-clipboard-check me-2"></i>
                Safety Tests
                <span class="badge bg-secondary ms-2">{{ tabCounts?.safetyTestResults }}</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'seasons'"
                type="button"
                role="tab"
                (click)="setActiveTab('seasons')"
              >
                <i class="bi bi-calendar-range me-2"></i>
                Seasons
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'analyses'"
                type="button"
                role="tab"
                (click)="setActiveTab('analyses')"
              >
                <i class="bi bi-graph-up-arrow me-2"></i>
                Analyses
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'activity'"
                type="button"
                role="tab"
                (click)="setActiveTab('activity')"
              >
                <i class="bi bi-clock-history me-2"></i>
                Activity
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'reports'"
                type="button"
                role="tab"
                (click)="setActiveTab('reports')"
              >
                <i class="bi bi-bar-chart me-2"></i>
                Reports
              </button>
            </li>
          </ul>
        </div>

        <!-- Tab Content -->
        <div class="card-body p-3 p-lg-4">
          <!-- Overview Tab -->
          <div *ngIf="activeTab === 'overview'" class="tab-pane">
            <app-organization-overview-tab
              [organization]="organization"
              [ownerProfile]="ownerProfile"
              [chiefSupervisorProfile]="chiefSupervisorProfile"
              [stats]="stats"
              (membershipChanged)="onMembershipChanged($event)"
            ></app-organization-overview-tab>
          </div>

          <!-- Members Tab -->
          <div *ngIf="activeTab === 'members'" class="tab-pane">
            <app-organization-members-tab
              [organizationId]="organizationId"
              [organizationName]="organization.name"
              [members]="members"
              [ownerProfileId]="organization.ownerProfileId"
              [chiefSupervisorProfileId]="organization.chiefSupervisorProfileId"
              (membersUpdated)="onMembersUpdated()"
            ></app-organization-members-tab>
          </div>

          <!-- Drones Tab -->
          <div *ngIf="activeTab === 'drones'" class="tab-pane">
            <app-organization-drones-tab
              [organizationId]="organizationId"
              [organizationName]="organization.name"
              (dronesChanged)="onDronesChanged()"
            ></app-organization-drones-tab>
          </div>

          <!-- Certifications Tab -->
          <div *ngIf="activeTab === 'certifications'" class="tab-pane">
            <app-certifications-card
              [organizationId]="organizationId"
            ></app-certifications-card>
          </div>

          <!-- Spray Records Tab -->
          <div *ngIf="activeTab === 'sprayRecords'" class="tab-pane">
            <app-organization-spray-records-tab
              [organizationId]="organizationId"
            ></app-organization-spray-records-tab>
          </div>

          <!-- Customers Tab -->
          <div *ngIf="activeTab === 'customers'" class="tab-pane">
            <app-organization-customers-tab
              [organizationId]="organizationId"
            ></app-organization-customers-tab>
          </div>

          <!-- Safety Tests Tab -->
          <div *ngIf="activeTab === 'safetyTests'" class="tab-pane">
            <app-organization-test-results-tab
              [organizationId]="organizationId"
            ></app-organization-test-results-tab>
          </div>

          <!-- Seasons Tab -->
          <div *ngIf="activeTab === 'seasons'" class="tab-pane">
            <app-organization-seasons-tab
              [organizationId]="organizationId"
            ></app-organization-seasons-tab>
          </div>

          <!-- Analyses Tab -->
          <div *ngIf="activeTab === 'analyses'" class="tab-pane">
            <app-organization-analyses-tab
              [organizationId]="organizationId"
            ></app-organization-analyses-tab>
          </div>

          <!-- Activity Tab -->
          <div *ngIf="activeTab === 'activity'" class="tab-pane">
            <app-organization-activity-tab
              [organizationId]="organizationId"
            ></app-organization-activity-tab>
          </div>

          <!-- Reports Tab -->
          <div *ngIf="activeTab === 'reports'" class="tab-pane">
            <app-organization-reports-tab
              [organizationId]="organizationId"
            ></app-organization-reports-tab>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Archive/Unarchive Confirmation Modal -->
<app-confirmation-modal
  *ngIf="showConfirmModal && confirmModalAction === 'archive'"
  title="Archive Organization"
  [message]="'Are you sure you want to archive \'' + organization?.name + '\'? The organization will be hidden from the default list but can be recovered later.'"
  confirmLabel="Archive"
  processingLabel="Archiving..."
  variant="warning"
  icon="bi bi-archive"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmAction()"
  (cancelled)="onCancelConfirm()">
</app-confirmation-modal>

<app-confirmation-modal
  *ngIf="showConfirmModal && confirmModalAction === 'unarchive'"
  title="Restore Organization"
  [message]="'Are you sure you want to restore \'' + organization?.name + '\'? The organization will be visible in the default list again.'"
  confirmLabel="Restore"
  processingLabel="Restoring..."
  variant="warning"
  icon="bi bi-archive"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmAction()"
  (cancelled)="onCancelConfirm()">
</app-confirmation-modal>

<app-confirmation-modal
  *ngIf="showConfirmModal && confirmModalAction === 'delete'"
  title="Delete Organization"
  [message]="'Are you sure you want to delete \'' + organization?.name + '\'? This action cannot be undone.'"
  confirmLabel="Delete"
  processingLabel="Deleting..."
  variant="danger"
  icon="bi bi-trash"
  [processing]="confirmModalProcessing"
  (confirmed)="onConfirmAction()"
  (cancelled)="onCancelConfirm()">
</app-confirmation-modal>
